import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuotePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', property_address: '',
    property_type: '', service_needed: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 pt-20" data-testid="quote-success">
        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-12 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
          <h2 className="font-outfit text-2xl font-bold text-stone-900 mb-3">Quote Request Received!</h2>
          <p className="text-stone-600 mb-8">We'll review your request and get back to you within 24 hours at <strong>{form.email}</strong>.</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full px-8 py-6 font-semibold"
            data-testid="quote-back-home"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-6" data-testid="quote-page">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-emerald-900 transition-colors mb-8 text-sm" data-testid="quote-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>

        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-900/70 mb-3">Free Quote</p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 mb-3">
            Get a Quote for Your Property
          </h1>
          <p className="text-stone-600">Fill out the form below and our team will provide you with a personalized quote.</p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="John Doe" className="text-stone-900" data-testid="quote-name" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required placeholder="john@example.com" className="text-stone-900" data-testid="quote-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required placeholder="(555) 123-4567" className="text-stone-900" data-testid="quote-phone" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Property Type</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({...form, property_type: v})}>
                  <SelectTrigger className="text-stone-900" data-testid="quote-property-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="multi-family">Multi-Family</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Property Address</Label>
              <Input value={form.property_address} onChange={(e) => setForm({...form, property_address: e.target.value})} required placeholder="123 Main St, City, State" className="text-stone-900" data-testid="quote-address" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Service Needed</Label>
              <Select value={form.service_needed} onValueChange={(v) => setForm({...form, service_needed: v})}>
                <SelectTrigger className="text-stone-900" data-testid="quote-service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="property-management">Property Management</SelectItem>
                  <SelectItem value="sell-property">Sell My Property</SelectItem>
                  <SelectItem value="buy-property">Buy a Property</SelectItem>
                  <SelectItem value="investment-consulting">Investment Consulting</SelectItem>
                  <SelectItem value="tenant-screening">Tenant Screening</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Additional Details</Label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({...form, message: e.target.value})}
                rows={4}
                placeholder="Any additional information about your property or needs..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-stone-900 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="quote-message"
              />
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="quote-error">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full py-6 font-semibold text-base shadow-lg shadow-emerald-900/20 transition-all"
              data-testid="quote-submit"
            >
              {loading ? 'Submitting...' : 'Submit Quote Request'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
